import { useSegmentDemand } from '../../hooks/useCalculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { formatPercent, formatPrice } from '../../lib/formatters';
import { PRICE_TO_RATE } from '../../data/conjointData';
import { InfoButton } from '../common/Tooltip';

export function SegmentDemandTable() {
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const segments = useSegmentDemand();
  const totalWeighted = segments.reduce((s, seg) => s + seg.weightedDemand, 0);
  const overallRate = PRICE_TO_RATE[selectedPrice] ?? 0;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Segment Demand at {formatPrice(selectedPrice)}</h3>
        <InfoButton helpKey="segmentDemandTable" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Weighted segment contributions to overall choice rate</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-secondary">
              <th className="text-left py-2 px-2 font-medium">Segment</th>
              <th className="text-right py-2 px-2 font-medium">Weight</th>
              <th className="text-right py-2 px-2 font-medium">Choice Rate</th>
              <th className="text-right py-2 px-2 font-medium"><span className="inline-flex items-center gap-1">Market Contribution <InfoButton helpKey="weightedDemand" size={10} /></span></th>
              <th className="text-right py-2 px-2 font-medium">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {segments.map(seg => (
              <tr key={seg.segmentName} className="border-b border-border/50 transition-colors"
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                <td className="py-2 px-2 text-primary">{seg.segmentName}</td>
                <td className="text-right py-2 px-2 font-mono text-secondary">{formatPercent(seg.weight)}</td>
                <td className="text-right py-2 px-2 font-mono text-primary">{formatPercent(seg.choiceRate)}</td>
                <td className="text-right py-2 px-2 font-mono text-primary">{formatPercent(seg.weightedDemand)}</td>
                <td className="text-right py-2 px-2 font-mono text-muted">{totalWeighted > 0 ? formatPercent(seg.weightedDemand / totalWeighted) : '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td className="py-2 px-2 text-primary font-semibold">Total</td>
              <td className="text-right py-2 px-2 font-mono text-secondary">100.0%</td>
              <td className="text-right py-2 px-2 font-mono text-muted">—</td>
              <td className="text-right py-2 px-2 font-mono text-chart-teal font-semibold">{formatPercent(totalWeighted)}</td>
              <td className="text-right py-2 px-2 font-mono text-muted">100.0%</td>
            </tr>
            <tr>
              <td colSpan={3} className="py-1 px-2 text-xs text-muted">Overall concept-level rate (from study)</td>
              <td className="text-right py-1 px-2 font-mono text-xs text-muted">{formatPercent(overallRate)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
